import { preserveCampaignParameters } from "@/lib/acquisition/shared";
import { SHORT_LINK_PATH_PREFIX } from "./config";

/**
 * Client-side resolution of `/l/<code>` to the page it points at, so a native
 * deep link can be followed with `router.push` instead of a document
 * navigation. See `src/app/api/short-links/[code]/route.ts` for why only short
 * links get this treatment.
 */

/** How long to wait before giving up and letting the caller fall back. */
export const SHORT_LINK_RESOLVE_TIMEOUT_MS = 2500;

export function shortLinkCodeFromPath(pathname: string): string | null {
  if (!pathname.startsWith(SHORT_LINK_PATH_PREFIX)) return null;
  const rest = pathname.slice(SHORT_LINK_PATH_PREFIX.length);
  const code = rest.split(/[?#/]/, 1)[0] ?? "";
  return code || null;
}

/**
 * A destination is only usable if it is a same-origin absolute path. The mint
 * path already restricts targets to `SHAREABLE_TARGET_PREFIXES`, so this is
 * belt-and-braces — but it is the one place a value from the network becomes a
 * navigation, and `//evil.example` is a path by `startsWith("/")` alone.
 */
export function isSafeResolvedPath(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.startsWith("/\\")
  );
}

/**
 * Returns the destination path, or null when the code is unknown, the request
 * fails, or it takes longer than a document navigation would have. Never
 * throws: every null means "fall back to the document navigation", which is
 * exactly what the app did before this existed.
 */
export async function resolveShortLinkPath(
  pathname: string,
  timeoutMs: number = SHORT_LINK_RESOLVE_TIMEOUT_MS,
): Promise<string | null> {
  const code = shortLinkCodeFromPath(pathname);
  if (!code) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(
      `/api/short-links/${encodeURIComponent(code)}`,
      { signal: controller.signal, headers: { accept: "application/json" } },
    );
    if (!response.ok) return null;

    const body: unknown = await response.json();
    const path =
      typeof body === "object" && body !== null && "path" in body
        ? (body as { path?: unknown }).path
        : undefined;

    return isSafeResolvedPath(path)
      ? preserveCampaignParameters(path, pathname)
      : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
